import React from "react";

import { cn } from "../../../common/u1/cn";
import { Button } from "../../../common/u1/Button";
import { Input } from "../../../common/u1/Input";
import { FieldLabel } from "../../../common/u1/FieldLabel";
import { ReadOnlyField } from "../../../common/u1/ReadOnlyField";
import { PhoneCountrySelect } from "../../../common/u1/PhoneCountrySelect";
import PhoneNumberInput from "../../../common/PhoneNumberInput";
import { PencilIcon, CloseIcon } from "../../../../assets/ui/icons";
import { ProfileAvatar } from "./ProfileAvatar";
import { useUserProfile } from "./useUserProfile";
import { DESIGNATION_MAX_LENGTH, canRemoveImage, displayValue } from "./profileFields";

/* u1 User Profile. Visual uplift only. Every rule lives in useUserProfile, transcribed from components/admin/Dashboard/profile/UserProfileForm.jsx: the same... */
export const UserProfileForm = () => {
  const profile = useUserProfile();
  const { userInfo, isEditMode, values, setField, error } = profile;

  /* Transcribed: out of edit mode every field shows the SAVED value. */
  const shown = (field, saved) =>
    displayValue({ isEditMode, draft: values[field], saved });

  const errorFor = (field) => (error?.field === field ? error.message : undefined);

  return (
    <section className="rounded-[14px] border border-[var(--line)] bg-white p-5 sm:p-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="m-0 text-[20px] font-bold text-[var(--ink)] font-sans">
            User Profile
          </h1>
          <p className="m-0 mt-1 text-[13px] text-[var(--muted)] font-sans">
            How you appear to the businesses you deal with on TREVEX.
          </p>
        </div>

        {isEditMode ? (
          <Button variant="ghost" size="sm" className="gap-1.5" onClick={profile.cancelEdit}>
            <CloseIcon size={13} />
            Cancel
          </Button>
        ) : (
          <Button variant="outline" size="sm" className="gap-1.5" onClick={profile.startEdit}>
            <PencilIcon size={13} />
            Edit profile
          </Button>
        )}
      </header>

      <form onSubmit={profile.save} noValidate>
        {/* The photo is a column of its own rather than a negative-margin
            overlay, so it cannot land on top of the fields. */}
        <div
          className={cn(
            "mt-6 grid grid-cols-1 items-start gap-6",
            "lg:grid-cols-[minmax(0,1fr)_220px]"
          )}
        >
          <div>
            <h2 className="m-0 text-[15px] font-bold text-[var(--ink)] font-sans">
              Personal information
            </h2>

            <div className="mt-3.5 flex flex-col gap-4">
              <Input
                label="Full Name"
                id="u1-profile-name"
                value={shown("name", userInfo?.name)}
                readOnly={!isEditMode}
                onChange={(e) => setField("name", e.target.value)}
                error={errorFor("name")}
                placeholder="Your name"
                autoComplete="name"
              />

              {/* Not editable in any mode. */}
              <ReadOnlyField label="Email Address" value={userInfo?.email} />

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Input
                  label="Designation"
                  id="u1-profile-designation"
                  value={shown("designation", userInfo?.individual?.[0]?.designation)}
                  readOnly={!isEditMode}
                  maxLength={DESIGNATION_MAX_LENGTH}
                  onChange={(e) => setField("designation", e.target.value)}
                  error={errorFor("designation")}
                  placeholder="Your role"
                />
                <Input
                  label="Business Name"
                  id="u1-profile-business"
                  value={shown(
                    "employer_company_name",
                    userInfo?.individual?.[0]?.employer_company_name
                  )}
                  readOnly={!isEditMode}
                  onChange={(e) => setField("employer_company_name", e.target.value)}
                  error={errorFor("employer_company_name")}
                  placeholder="Who you work for"
                />
              </div>
            </div>
          </div>

          <ProfileAvatar
            className="lg:pt-8"
            userInfo={userInfo}
            busy={profile.imageBusy}
            canRemove={canRemoveImage(userInfo)}
            onSelect={profile.uploadImage}
            onRemove={profile.removeImage}
          />
        </div>

        <div className="mt-6 border-t border-[var(--line)] pt-6">
          <h2 className="m-0 text-[15px] font-bold text-[var(--ink)] font-sans">
            Contact information{" "}
            <span className="font-medium text-[var(--muted)]">(optional)</span>
          </h2>

          <div className="mt-3.5 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* PhoneNumberInput renders its own control, so the caption is a
                span rather than a label pointing at nothing. Each one needs its
                own id — the live form gives both `id="mobile"`. */}
            <div>
              <FieldLabel as="span">Mobile Number</FieldLabel>
              <PhoneNumberInput
                id="u1-profile-mobile"
                phone={shown("mobile", values.mobile)}
                setPhone={(next) => setField("mobile", next)}
                border
                verifyPhoneNumber={false}
                readOnly={!isEditMode}
                countrySelect={PhoneCountrySelect}
                defaultCountry="AE"
              />
            </div>

            <div>
              <FieldLabel as="span">Alternate mobile number (optional)</FieldLabel>
              <PhoneNumberInput
                id="u1-profile-alt-mobile"
                phone={shown("alternateMobile", values.alternateMobile)}
                setPhone={(next) => setField("alternateMobile", next)}
                border
                verifyPhoneNumber={false}
                readOnly={!isEditMode}
                countrySelect={PhoneCountrySelect}
                defaultCountry="AE"
              />
            </div>
          </div>
        </div>

        {/* Transcribed: the save only exists in edit mode. */}
        {isEditMode && (
          <Button
            type="submit"
            variant="coralSolid"
            className="mt-6 w-full"
            disabled={profile.saving}
          >
            {profile.saving ? "Saving…" : "Save changes"}
          </Button>
        )}
      </form>
    </section>
  );
};

export default UserProfileForm;
